import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';
import type { OralExamReadiness as OralExamReadinessType } from '@/common/api/models/oralExamReadiness';
import type { OralExamWaitingTimePhase } from '@/common/utils/oralExam/studentJoinWindow';

export type StartGateReasonItem = {
  studentName: string;
  readiness?: OralExamReadinessType;
};

/**
 * How the start gate renders a reason:
 * - `action`: the supervisor must do something (warning icon).
 * - `waiting`: normal wait on students (neutral clock icon).
 * - `checking`: the app is checking something (spinner).
 * - `info`: a plain fact (neutral info icon).
 */
export type StartGateReasonKind = 'action' | 'waiting' | 'checking' | 'info';

export type StartGateCopy = {
  key: string;
  defaultValue: string;
  values?: Record<string, string | number>;
};

export type StartGateReason = {
  kind: StartGateReasonKind;
  testID: string;
  primary: StartGateCopy;
};

export type StartGateReasonInput = {
  readiness: StartGateReasonItem[];
  /** Null when the slot has no scheduled start (dry run). */
  timePhase: OralExamWaitingTimePhase | null;
  /** Lecturer join window closed (start + grace, honours the dev bypass). */
  joinWindowClosed: boolean;
  examinerChecking: boolean;
  /** Formatted join-window close time, e.g. "09:42 AM". */
  graceEndTime?: string;
};

const KEY_PREFIX = 'oralExam.lecturer.startGate';

export function isNeverJoinedReadiness(readiness?: OralExamReadinessType): boolean {
  return readiness === OralExamReadiness.not_joined
    || readiness === OralExamReadiness.presence_unknown;
}

export function formatStudentNameList(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** One name, or a count: the reason is a one-liner beside the button; rows carry the names. */
function namesOf(items: StartGateReasonItem[]): string {
  if (items.length > 1) return `${items.length} students`;
  return formatStudentNameList(items.map((item) => item.studentName));
}

/**
 * The single most important reason the supervisor cannot (yet) start, or null
 * when nothing needs saying. Mirrors the start rules in `StartGate`: before the
 * join window closes everyone booked must be ready; after it closes only an ID
 * waiting for review blocks. Session-ending states (supervisor no-show,
 * technical cancel) and the AI-examiner-unavailable alert are rendered by the
 * caller before this reason is consulted.
 */
export function resolveStartGateReason({
  readiness,
  timePhase,
  joinWindowClosed,
  examinerChecking,
  graceEndTime,
}: StartGateReasonInput): StartGateReason | null {
  const idReview = readiness.filter(
    (item) => item.readiness === OralExamReadiness.awaiting_id_review,
  );
  if (idReview.length > 1) {
    return {
      kind: 'action',
      testID: 'start-gate-id-review-hint',
      primary: {
        key: `${KEY_PREFIX}.idReviewBlockHintMany`,
        defaultValue: 'Confirm student IDs to start.',
      },
    };
  }
  if (idReview.length === 1) {
    return {
      kind: 'action',
      testID: 'start-gate-id-review-hint',
      primary: {
        key: `${KEY_PREFIX}.idReviewBlockHint`,
        defaultValue: "Review {{studentName}}'s ID to start.",
        values: { studentName: idReview[0].studentName },
      },
    };
  }

  if (examinerChecking) {
    return {
      kind: 'checking',
      testID: 'start-gate-examiner-checking',
      primary: {
        key: `${KEY_PREFIX}.examinerChecking`,
        defaultValue: 'Checking AI Examiner availability…',
      },
    };
  }

  const neverJoined = readiness.filter((item) => isNeverJoinedReadiness(item.readiness));
  if (!joinWindowClosed && neverJoined.length > 0) {
    if (timePhase === 'during_grace' && graceEndTime) {
      return {
        kind: 'waiting',
        testID: 'start-gate-grace-hint',
        primary: {
          key: `${KEY_PREFIX}.graceWaitingUntil`,
          defaultValue: 'Waiting until {{time}} for remaining students to join.',
          values: { time: graceEndTime },
        },
      };
    }
    return {
      kind: 'waiting',
      testID: 'start-gate-waiting-join',
      primary: {
        key: `${KEY_PREFIX}.waitingToJoin`,
        defaultValue: 'Waiting for {{studentNames}} to join.',
        values: { studentNames: namesOf(neverJoined) },
      },
    };
  }

  const watchingOrientation = readiness.filter(
    (item) => item.readiness === OralExamReadiness.watching_orientation,
  );
  if (watchingOrientation.length > 0) {
    return {
      kind: 'waiting',
      testID: 'start-gate-orientation-warning',
      primary: {
        key: `${KEY_PREFIX}.orientationWarning`,
        defaultValue: 'Waiting for {{studentNames}} to finish the orientation.',
        values: { studentNames: namesOf(watchingOrientation) },
      },
    };
  }

  if (!joinWindowClosed) {
    const checkingIn = readiness.filter(
      (item) => !isNeverJoinedReadiness(item.readiness)
        && item.readiness !== OralExamReadiness.ready_to_start,
    );
    if (checkingIn.length > 0) {
      return {
        kind: 'waiting',
        testID: 'start-gate-checkin-hint',
        primary: {
          key: `${KEY_PREFIX}.waitingCheckIn`,
          defaultValue: 'Waiting for {{studentNames}} to finish check-in.',
          values: { studentNames: namesOf(checkingIn) },
        },
      };
    }
    return null;
  }

  const anyReady = readiness.some((item) => item.readiness === OralExamReadiness.ready_to_start);
  if (!anyReady && readiness.length > 0) {
    return {
      kind: 'info',
      testID: 'start-gate-nobody-ready',
      primary: {
        key: `${KEY_PREFIX}.joinClosedNobody`,
        defaultValue: 'No student is ready to start.',
      },
    };
  }
  return null;
}
